import { FC, lazy } from 'react';
import { ContractManagementRequest } from 'src/apis/contract-management/use-contract-management';
import { Shipper } from 'src/apis/monolith/types';
import { Alert } from 'src/components/feedback/alert';
import { PageSpinner } from 'src/components/spinner-container';
import { isNetworkError, Successful } from 'src/hooks/use-axios';
import {
  AssignedShippersRequestParams,
  useAssignedShippersPage,
} from 'src/pages/shippers/details/information/use-assigned-shippers-page';
import { ShipperPlatformIdsRequest } from 'src/pages/shippers/use-shipper-platform-ids';
import { useMemoOne } from 'use-memo-one';

const Page = lazy(() => import('./page'));

export const ShipperInformation: FC<{
  shipper: Shipper;
  contractManagement: ContractManagementRequest;
  shipperPlatformIds: Successful<ShipperPlatformIdsRequest>;
}> = ({ shipper, contractManagement, shipperPlatformIds }) => {
  const eic = shipper.identifierList.filter((i) => i.type === 'EIC')[0].id;
  const requestParams = useMemoOne<AssignedShippersRequestParams>(
    () => ({ eic }),
    [eic]
  );
  const assignedShipper = useAssignedShippersPage(requestParams);

  if (!assignedShipper.pending && assignedShipper.error) {
    return (
      <Alert type="error">
        Sorry, an unknown error occurred. Please, check back later.
      </Alert>
    );
  }

  if (
    !assignedShipper.response ||
    (!contractManagement.response && !isNetworkError(contractManagement.error))
  ) {
    return <PageSpinner />;
  }

  if (
    !assignedShipper.response.data ||
    !Array.isArray(assignedShipper.response.data.data) ||
    !(assignedShipper.response.data.data.length > 0) ||
    assignedShipper.response.data.data[0].state === 'CANCELED'
  ) {
    return (
      <Alert type="info">
        {shipper.name} did not request assignment with you on the platform or
        the assignment has been already canceled.
      </Alert>
    );
  }

  return (
    <Page
      shipper={shipper}
      assignedShipper={assignedShipper.response.data.data[0]}
      contractManagement={contractManagement}
      assignedShippersRequest={assignedShipper}
      requestParams={requestParams}
      shipperPlatformIds={shipperPlatformIds}
    />
  );
};
